/**
 * Vendas assinadas aguardando o PCP (Capacidade da Fábrica, decisões 8 e 11):
 * o comercial registra a venda assim que o contrato é assinado, ela ocupa
 * lugar na fila projetada até o PCP ligá-la ao pedido digitado na planilha.
 *
 * Uma request para a página inteira, num cache de módulo (mesmo desenho de
 * `useExcecoesCapacidade`): a lista e a aba Vendas assinadas leem o mesmo
 * estado, e cada ação atualiza o cache no lugar — ligar ou cancelar tira a
 * venda da fila na hora, sem reler a lista (só relê quando o backend diz que
 * a venda já mudou: ver `agirNaVenda`).
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";
import {
  cancelarVendaAssinada,
  ligarVendaAssinada,
  listarVendasAssinadas,
  registrarVendaAssinada,
} from "../services/capacidade";

/** Mesmos gates do backend (`capacidade/services.py`). */
export const PERM_VER_VENDAS = "capacidade_fabrica:view";
export const PERM_REGISTRAR_VENDA = "capacidade_fabrica:registrar_venda";
export const PERM_LIGAR_VENDA = "capacidade_fabrica:ligar_venda";

const ESTADO_AGUARDANDO = "aguardando";
const VALIDADE_MS = 60_000;
const VAZIO = Object.freeze({
  vendas: Object.freeze([]),
  podeRegistrar: false,
  podeLigar: false,
  carregado: false,
  erro: null,
});

const cache = { dono: null, estado: VAZIO, lidoEm: 0, emAndamento: null, request: null };
const ouvintes = new Set();

function publicar(estado) {
  cache.estado = estado;
  ouvintes.forEach((ouvinte) => ouvinte(estado));
}

function buscar(request, dono) {
  if (cache.emAndamento && cache.dono === dono) return cache.emAndamento;
  cache.dono = dono;
  cache.request = request;
  // Promise.resolve: um `request` que falhe antes de devolver promessa vira
  // rejeição tratada abaixo, e não erro solto dentro do efeito.
  cache.emAndamento = Promise.resolve()
    .then(() => listarVendasAssinadas(request))
    .then((resposta) => {
      if (cache.dono !== dono) return;
      cache.lidoEm = Date.now();
      publicar({
        vendas: Array.isArray(resposta?.vendas) ? resposta.vendas : [],
        podeRegistrar: Boolean(resposta?.pode_registrar),
        podeLigar: Boolean(resposta?.pode_ligar),
        carregado: true,
        erro: null,
      });
    })
    // Sem a lista a página segue só com a planilha; a aba diz que não leu. A
    // próxima tentativa só depois da validade (não repete em loop).
    .catch((erro) => {
      cache.lidoEm = Date.now();
      if (cache.dono === dono) {
        publicar({
          ...cache.estado,
          carregado: true,
          erro: erro?.message || "Não foi possível ler as vendas assinadas.",
        });
      }
    })
    .finally(() => {
      cache.emAndamento = null;
    });
  return cache.emAndamento;
}

/** Releitura imediata (botão "tentar de novo" da aba). */
export function recarregarVendasAssinadas() {
  if (!cache.request) return Promise.resolve();
  cache.lidoEm = 0;
  return buscar(cache.request, cache.dono);
}

/** Só para testes: esquece o que foi lido. */
export function limparCacheDeVendasAssinadas() {
  cache.dono = null;
  cache.estado = VAZIO;
  cache.lidoEm = 0;
  cache.emAndamento = null;
  cache.request = null;
}

/** A lista do cache guarda só as aguardando: a que mudou de estado sai. */
function aplicarVenda(dono, venda) {
  if (cache.dono !== dono || !venda?.id) return;
  const outras = cache.estado.vendas.filter((v) => v.id !== venda.id);
  publicar({
    ...cache.estado,
    vendas: venda.estado === ESTADO_AGUARDANDO ? [...outras, venda] : outras,
  });
}

/**
 * 409 = a venda já não está aguardando (outra pessoa ligou ou cancelou); 404 =
 * ela sumiu. Nos dois casos a lista do cache está velha: relê para a venda sair
 * da fila e dos botões, e devolve o erro para o painel mostrar o motivo.
 */
const STATUS_VENDA_JA_MUDOU = Object.freeze([404, 409]);

async function agirNaVenda(dono, acao) {
  try {
    const venda = await acao();
    aplicarVenda(dono, venda);
    return venda;
  } catch (erro) {
    if (STATUS_VENDA_JA_MUDOU.includes(erro?.status) && cache.dono === dono) {
      recarregarVendasAssinadas();
    }
    throw erro;
  }
}

/**
 * `{vendas, podeRegistrar, podeLigar, carregado, erro, permitido,
 * registrar(corpo), ligar(vendaId, pedidoPlanilha), cancelar(vendaId, motivo),
 * recarregar}`. `vendas` = as aguardando, da mais antiga para a mais nova.
 * As ações rejeitam com a mensagem do backend em `err.message`.
 */
export default function useVendasAssinadas({ ativo = true } = {}) {
  const { request, usuario, hasPermission } = useAuth() || {};
  const permitido =
    ativo && Boolean(usuario) && typeof hasPermission === "function" && hasPermission(PERM_VER_VENDAS);
  const dono = usuario?.id ?? null;
  const [estado, setEstado] = useState(() => (cache.dono === dono ? cache.estado : VAZIO));

  useEffect(() => {
    if (!permitido) return undefined;
    ouvintes.add(setEstado);
    if (cache.dono !== dono) {
      cache.estado = VAZIO;
      cache.lidoEm = 0;
    }
    setEstado(cache.estado);
    if (Date.now() - cache.lidoEm > VALIDADE_MS) buscar(request, dono);
    return () => {
      ouvintes.delete(setEstado);
    };
  }, [permitido, request, dono]);

  const registrar = useCallback(
    async (corpo) => {
      const venda = await registrarVendaAssinada(request, corpo);
      aplicarVenda(dono, venda);
      return venda;
    },
    [request, dono],
  );

  const ligar = useCallback(
    (vendaId, pedidoPlanilha) =>
      agirNaVenda(dono, () => ligarVendaAssinada(request, vendaId, { pedidoPlanilha })),
    [request, dono],
  );

  const cancelar = useCallback(
    (vendaId, motivo) => agirNaVenda(dono, () => cancelarVendaAssinada(request, vendaId, { motivo })),
    [request, dono],
  );

  const acoes = { registrar, ligar, cancelar, recarregar: recarregarVendasAssinadas };
  // Sem a permissão de ver a página não há o que esperar: `carregado` vem
  // verdadeiro e `permitido` falso, para quem mostra a aba saber esconder.
  return permitido
    ? { ...estado, permitido: true, ...acoes }
    : { ...VAZIO, carregado: true, permitido: false, ...acoes };
}
